import * as Sentry from '@sentry/browser';

// LOCAL IMPORTS
import {
    saveAuthCredentials,
    getRefreshToken,
    forgetAuthCredentials,
} from 'ggApp/modules/auth/utils';
import { shutdownIntercom } from 'ggApp/modules/intercom/service';
import { getCookiesFromConfig, injectCookiesToConfig } from 'ggApp/utils/CookiesManager';
import { isAppUser } from 'ggApp/routes/_wrap/MobileWrapper/utils';

import createAxiosInstance from './axiosFactory';

// CONSTANTS
const UNAUTHORIZED_HTTP_STATUS = 401;
// TODO: refactor refresh token mechanism, to use WWW-Authenticate header
// const REVOKED_TOKEN_AUTHENTICATION_HEADER =
//     'Bearer realm="Doorkeeper", error="invalid_token", error_description="The access token was revoked"';

// MODULE GLOBAL VARIABLES
const axiosInstance = createAxiosInstance();

let refreshTokenPromise = null;

// MODULE METHODS
function refreshAccessToken(refreshToken, cookiesManager) {
    return new Promise((resolve, reject) => {
        if (!refreshToken) {
            return resolve({});
        }

        const config = {
            grant_type: 'refresh_token',
            refresh_token: refreshToken,
        };

        injectCookiesToConfig(cookiesManager, config);
        axiosInstance
            .post('/oauth/token', config)
            .then((response) => {
                const { data } = response;
                // eslint-disable-next-line camelcase
                const { access_token, refresh_token, expires_in } = data;

                const authCredentials = {
                    access_token,
                    refresh_token,
                    expires_in,
                };

                saveAuthCredentials(access_token, refresh_token, expires_in, cookiesManager);

                return authCredentials;
            })
            .then(resolve)
            .catch(reject)
            .finally(() => {
                refreshTokenPromise = null;
            });

        return null;
    });
}

function interceptorErrorHandler(error) {
    return new Promise((resolve, reject) => {
        const { config: originalRequestConfig, status, data, request } = error.response ?? {};

        if (status !== UNAUTHORIZED_HTTP_STATUS) {
            return reject(error);
        }

        const cookiesManager = getCookiesFromConfig(originalRequestConfig);
        const refreshToken = getRefreshToken(cookiesManager);
        // TODO: refactor refresh token mechanism, to use WWW-Authenticate header:
        // const authenticateHeader = headers['WWW-Authenticate'];
        // const isRevokedToken = authenticateHeader === REVOKED_TOKEN_AUTHENTICATION_HEADER;
        const twoFactorAuthIsRequired = Boolean(data?.['2fa_required']);

        if (!refreshToken || twoFactorAuthIsRequired) {
            return reject(error);
        }

        if (!refreshTokenPromise) {
            refreshTokenPromise = refreshAccessToken(refreshToken, cookiesManager);
        }
        // to take new token from cookies in the headersInterceptor
        originalRequestConfig.headers.Authorization = undefined;

        refreshTokenPromise
            .then(() => axiosInstance.request(originalRequestConfig))
            .then(resolve)
            .catch(() => {
                if (!__IS_SERVER__) {
                    shutdownIntercom();
                }

                forgetAuthCredentials(cookiesManager);

                if (isAppUser(originalRequestConfig?.headers)) {
                    Sentry.configureScope((scope) => {
                        scope.setExtra('request', request);
                        scope.setExtra('originalRequestConfig', originalRequestConfig);
                        scope.setExtra('data', data);
                        scope.setExtra('cookiesManager', cookiesManager);
                        scope.setExtra('error', error);
                        Sentry.captureException(
                            `App user force logout in refreshAccessToken: ${error}`,
                        );
                    });
                }

                reject(error);
            });

        return null;
    });
}

export default function createRefreshTokenInterceptor() {
    return [(response) => response, interceptorErrorHandler];
}
